import { type FC, useEffect, useRef, useState } from "react";
import type Hls from "hls.js";
import { Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Player } from "@/shared/components/Player";
import type { Stream, StreamContent } from "@/partner/entities/streams";
import { useCreateStreamClipMutation } from "@/partner/entities/streams";

// Ровно те же длительности, что в референсной студии.
const GRAB_DURATIONS = [5, 10, 15, 30, 45, 60] as const;
// Пока эфир короче — резать нечего.
const MIN_OFFSET_SECONDS = 5;

interface StreamStudioLiveProps {
  content: StreamContent;
  stream: Stream;
  onCreated: () => void;
}

export const StreamStudioLive: FC<StreamStudioLiveProps> = ({
  content,
  stream,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const hlsRef = useRef<Hls | null>(null);

  const [createStreamClip, { loading }] = useCreateStreamClipMutation({
    // Cloudflare может отказать в клипе, пока последний сегмент эфира ещё не
    // сформирован (этот компонент рендерится только пока stream.status ===
    // "STARTED" — см. StreamStudioTab) — сырой текст ошибки в этом случае
    // партнёру ничего не скажет, показываем понятное сообщение вместо него.
    onError: (error) =>
      enqueueSnackbar(
        stream.status === "STARTED"
          ? t("streams.studio.liveClipRejected")
          : error.message,
        { variant: "error" }
      ),
  });

  // hlsRef is populated asynchronously by getHlsInstance and playingDate
  // only becomes available once the manifest has actual
  // EXT-X-PROGRAM-DATE-TIME tags — neither of those events re-renders this
  // component on its own. A 1s tick is enough to make the buttons enable
  // themselves shortly after playback actually starts, without hooking into
  // Player's more frequent timeupdate callback just for this.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => forceTick((tick) => tick + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Смещение текущей точки воспроизведения от начала эфира, в секундах.
   * hls.playingDate — wall-clock текущей точки, который hls.js выводит из
   * EXT-X-PROGRAM-DATE-TIME; через HTMLVideoElement его не достать.
   * Начало эфира берём с сервера (lastPublishAt) — оно не зависит от того,
   * скользящее у манифеста окно или растущее (DVR).
   *
   * Точность: lastPublishAt — момент, когда НАШ сервер узнал о подключении
   * энкодера, поэтому смещение может разойтись с истинным на секунду-две.
   * Для «схватить момент» это приемлемо: партнёр всегда может перерезать
   * точно по записи после эфира. Намеренный компромисс, не баг.
   */
  const currentOffsetSeconds = (): number | null => {
    const playingDate = hlsRef.current?.playingDate;
    if (!playingDate || !stream.lastPublishAt) return null;
    const offset =
      (playingDate.getTime() - new Date(stream.lastPublishAt).getTime()) / 1000;
    return offset > 0 ? offset : null;
  };

  const grab = async (duration: number) => {
    const offset = currentOffsetSeconds();
    if (offset === null) return;

    const { data } = await createStreamClip({
      variables: {
        input: {
          contentId: content.id,
          // Может уйти в минус (окно длиннее, чем прошло с начала эфира) —
          // намеренно не клампим здесь: ClipService.createForStream сам
          // укорачивает окно до начала эфира, но только если получит
          // отрицательный start; клампнув его тут, мы лишим бэкенд той самой
          // информации, которая нужна ему для этого.
          startSeconds: Math.round(offset - duration),
          durationSeconds: duration,
          title: t("streams.studio.defaultClipTitle"),
        },
      },
    });
    if (!data) return;
    enqueueSnackbar(t("streams.studio.createdSnackbar"), {
      variant: "success",
    });
    onCreated();
  };

  const offset = currentOffsetSeconds();
  const canGrab = offset !== null && offset >= MIN_OFFSET_SECONDS && !loading;

  return (
    <Stack spacing={2}>
      <Player
        src={stream.playback?.hlsUrl ?? null}
        poster={stream.playback?.thumbnailUrl ?? undefined}
        autoPlay
        muted
        hotkeys={false}
        getHlsInstance={(hls) => {
          hlsRef.current = hls;
        }}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {GRAB_DURATIONS.map((duration) => (
          <Button
            key={duration}
            variant="outlined"
            disabled={!canGrab}
            onClick={() => void grab(duration)}
          >
            {t("streams.studio.grabLast", { count: duration })}
          </Button>
        ))}
      </Stack>
      {!canGrab && (
        <Typography variant="caption" color="text.secondary">
          {t("streams.studio.notStartedYet")}
        </Typography>
      )}
    </Stack>
  );
};
